import { compactUrlsInUserMessage, sanitizeReferenceImagePublicUrlError } from './referenceImageSubmitValidation'

/**
 * 将接口/任务中的内部实现与平台前缀从用户可见文案中剥离。
 * 完整原文仍应记在后端或浏览器调试日志中排查。
 */

const FULL_MESSAGE_REPLACEMENTS: ReadonlyArray<{ pattern: RegExp; replacement: string }> = [
  {
    pattern: /^多米平台未启用或 API Key 未配置\s*$/u,
    replacement: 'AI 服务暂不可用，请稍后再试或联系管理员',
  },
  {
    pattern: /^未配置多米接口 Key[（(].*[）)]\s*$/u,
    replacement: '生图服务未正确配置，请稍后再试或联系管理员',
  },
  {
    pattern:
      /^多米超清未配置：\s*请启用\s*glyphicai\.ai\.platforms\.duomi\s*并填写\s*api-key\s*$/u,
    replacement: '超清服务未正确配置，请稍后再试或联系管理员',
  },
  {
    pattern: /^多米创建任务未返回任务 id\s*$/u,
    replacement: '创建任务未返回编号，请稍后再试',
  },
  {
    pattern: /^香蕉创建任务未返回任务 id\s*$/u,
    replacement: '创建任务未返回编号，请稍后再试',
  },
  {
    pattern: /^多米任务查询超时\s*$/u,
    replacement: '任务查询超时，请稍后再试',
  },
  {
    pattern: /^香蕉任务查询超时\s*$/u,
    replacement: '任务查询超时，请稍后再试',
  },
  {
    pattern: /^多米查询任务失败\s*$/u,
    replacement: '查询任务状态失败，请稍后再试',
  },
  {
    pattern: /^香蕉查询任务失败\s*$/u,
    replacement: '查询任务状态失败，请稍后再试',
  },
  {
    pattern: /^多米创建任务失败\s*$/u,
    replacement: '创建生图任务失败，请稍后再试',
  },
  {
    pattern: /^多米任务查询被中断\s*$/u,
    replacement: '任务查询被中断，请稍后再试',
  },
  {
    pattern: /^香蕉任务查询被中断\s*$/u,
    replacement: '任务查询被中断，请稍后再试',
  },
  {
    pattern: /^香蕉创建任务请求失败\s*$/u,
    replacement: '创建生图任务失败，请稍后再试',
  },
  {
    pattern: /^香蕉接口返回数据不是 JSON\s*$/u,
    replacement: '服务返回数据异常，请稍后再试',
  },
  {
    pattern: /^多米创建任务失败：返回数据不是 JSON\s*$/u,
    replacement: '服务返回数据异常，请稍后再试',
  },
  {
    pattern: /^多米查询任务失败：返回数据不是 JSON\s*$/u,
    replacement: '服务返回数据异常，请稍后再试',
  },
  {
    pattern: /^多米超清请求失败\s*$/u,
    replacement: '超清处理失败，请稍后再试',
  },
  {
    pattern: /^多米超清返回非 JSON\s*$/u,
    replacement: '超清服务返回异常，请稍后再试',
  },
  {
    pattern: /^多米超清未返回结果图 URL\s*$/u,
    replacement: '超清处理未完成，未返回图片地址',
  },
  {
    pattern: /^多米超清业务错误\s*$/u,
    replacement: '超清处理失败，请稍后再试',
  },
]

/** 按长度降序，优先匹配较长前缀，避免短规则吞掉长文案的一部分 */
const LEADING_PREFIX_PATTERNS: ReadonlyArray<RegExp> = [
  /^香蕉创建任务失败（HTTP \d+）\s*/u,
  /^香蕉查询任务失败[：:]\s*\d+\s*/u,
  /^多米查询任务失败[：:]\s*\d+\s*/u,
  /^香蕉任务失败[：:]\s*/u,
  /^多米任务失败[：:]\s*/u,
  /^香蕉创建任务失败[：:]\s*/u,
  /^多米创建任务失败[：:]\s*/u,
  /^香蕉查询任务失败[：:]\s*/u,
  /^多米查询任务失败[：:]\s*/u,
  /^多米超清业务错误[：:]\s*/u,
  /^Duomi\s*(task\s*)?failed[：:\s]+/iu,
]

function stripInternalSnippets(s: string): string {
  let out = s
  out = out.replace(/[（(]\s*glyphicai\.[^）)]*[）)]/gu, '')
  out = out.replace(/\bglyphicai\.[a-z0-9._-]+/giu, '')
  out = out.replace(/\bduomiapi\.com\b/giu, '')
  out = out.replace(/\s{2,}/gu, ' ')
  return out.trim()
}

function stripLeadingPrefixesRepeated(s: string): string {
  let out = s.trim()
  let prev = ''
  for (let i = 0; i < 12 && out !== prev; i++) {
    prev = out
    for (const p of LEADING_PREFIX_PATTERNS) {
      const next = out.replace(p, '').trim()
      if (next !== out) {
        out = next
        break
      }
    }
  }
  return out
}

const TECHNICAL_ERROR_PATTERNS: ReadonlyArray<RegExp> = [
  /java\.lang\./iu,
  /NoClassDefFoundError/iu,
  /ClassNotFoundException/iu,
  /Handler dispatch failed/iu,
  /org\.springframework\./iu,
  /online\.fromzwr\./iu,
  /Exception in thread/iu,
  /Caused by:/iu,
  /\bat\s+[\w.$]+\(/u,
  /NullPointerException/iu,
  /IllegalStateException/iu,
]

function looksLikeTechnicalErrorMessage(raw: string): boolean {
  return TECHNICAL_ERROR_PATTERNS.some((pattern) => pattern.test(raw))
}

function sanitizeTechnicalErrorMessage(raw: string): string | null {
  if (!looksLikeTechnicalErrorMessage(raw)) return null
  if (/NoClassDefFoundError|ClassNotFoundException/iu.test(raw)) {
    return '服务未完整部署，请重启后端后重试'
  }
  if (/^服务异常[：:]/u.test(raw)) {
    return '服务暂时不可用，请稍后再试'
  }
  return '操作失败，请稍后再试'
}

/**
 * @param raw 接口 message 或任务 errorMessage 原文
 */
export function sanitizeUserFacingErrorMessage(raw: string): string {
  const s0 = raw.trim()
  if (!s0) return ''

  const technical = sanitizeTechnicalErrorMessage(s0)
  if (technical) return technical

  const referenceUrlMsg = sanitizeReferenceImagePublicUrlError(s0)
  if (referenceUrlMsg) return referenceUrlMsg

  for (const { pattern, replacement } of FULL_MESSAGE_REPLACEMENTS) {
    if (pattern.test(s0)) return replacement
  }

  let s = stripInternalSnippets(s0)
  s = stripLeadingPrefixesRepeated(s)
  s = stripInternalSnippets(s)

  const out = compactUrlsInUserMessage(s.trim())
  /** 去掉「多米查询任务失败：404」等仅含内部前缀+状态码后，避免回退到暴露平台的原文 */
  if (!out && s0) return '操作失败，请稍后再试'
  return out
}

/** 从 catch 到的 unknown 解析用户可见文案（ApiError 已脱敏，其它 Error 会再走一层 sanitize） */
export function resolveUserFacingErrorMessage(error: unknown, fallback = '加载失败，请稍后再试'): string {
  if (typeof error === 'string') {
    const sanitized = sanitizeUserFacingErrorMessage(error)
    return sanitized || fallback
  }
  if (error && typeof error === 'object' && 'message' in error) {
    const raw = String((error as { message?: unknown }).message ?? '').trim()
    if (raw) {
      const sanitized = sanitizeUserFacingErrorMessage(raw)
      return sanitized || fallback
    }
  }
  return fallback
}
